import { Select } from '@zorro/design-system/v2/Select';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import {
  ALLOWANCE_MODEL_SOURCE_NOTE_MAX_LENGTH,
  PlanYearSetupFormFields,
} from './PlanYearSetupUtils';

type Props = {
  planYearSetupForm: UseFormReturn<PlanYearSetupFormFields>;
  employerId: string;
};

export const AllowanceModelSourceFields = ({
  planYearSetupForm,
  employerId,
}: Props) => {
  const { control, formState, setValue, watch } = planYearSetupForm;
  const sourceErrors = formState.errors.allowanceModelSource;
  const quoteId = watch('allowanceModelSource.quoteId');
  const planDesignId = watch('allowanceModelSource.planDesignId');

  const { data: quotes = [] } = useMonolithQuery({
    method: 'quotesControllerFindAll',
    params: [employerId],
  });

  const { data: planDesigns = [] } = useMonolithQuery({
    method: 'quotePlanDesignControllerFindAllForQuote',
    params: quoteId && [quoteId],
  });

  const quoteOptions = quotes.map(({ id, name }) => ({
    value: id,
    label: name,
  }));
  const planDesignOptions = planDesigns.flatMap(
    ({ id, name, isCompleted }, index) =>
      isCompleted
        ? [{ value: id, label: name || `Plan design #${index + 1}` }]
        : []
  );

  function handleQuoteChange(nextQuoteId: string | null) {
    const options = { shouldValidate: true, shouldDirty: true };
    setValue('allowanceModelSource.quoteId', nextQuoteId, options);
    setValue('allowanceModelSource.planDesignId', null, options);
  }

  return (
    <Stack gap="md" mt="lg">
      <Text size="md" fw={500}>
        Record where this model came from and what was changed manually, so
        others can trace it later
      </Text>

      <Controller
        control={control}
        name="allowanceModelSource.quoteId"
        render={({ field: { value } }) => (
          <Select
            label="Select quote"
            placeholder="Select quote"
            size="lg"
            data={quoteOptions}
            value={value ?? null}
            onChange={handleQuoteChange}
            clearable
            searchable
          />
        )}
      />

      {quoteId && (
        <Controller
          control={control}
          name="allowanceModelSource.planDesignId"
          render={({ field: { value, onChange, onBlur } }) => (
            <Select
              label="Select plan design"
              placeholder="Select plan design"
              size="lg"
              data={planDesignOptions}
              value={value ?? null}
              onChange={onChange}
              onBlur={onBlur}
              error={sourceErrors?.planDesignId?.message}
              required
            />
          )}
        />
      )}

      {quoteId && planDesignId && (
        <Controller
          control={control}
          name="allowanceModelSource.adjustmentNote"
          render={({ field: { value, onChange, onBlur } }) => (
            <Textarea
              label="Manual process description"
              placeholder={`e.g. base plan design applied to all employees, classed by state ("Employee, NY"), plus an "Owner" class at $0 allowance`}
              minRows={4}
              autosize
              maxLength={ALLOWANCE_MODEL_SOURCE_NOTE_MAX_LENGTH}
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              error={sourceErrors?.adjustmentNote?.message}
              required
            />
          )}
        />
      )}
    </Stack>
  );
};
